import React, { useRef, useState, useEffect } from 'react';
import ReactPlayer from 'react-player/lazy';
import useIsClient from 'hooks/useIsClient';
import { getVideoHeight } from './utils/getVideoHeight';
import PlayButton from 'components/WistiaVideo/PlayButton';
import PlayLabel from 'components/WistiaVideo/PlayLabel';
import { WistiaWrapper } from './styles';
import WistiaCta from 'components/WistiaVideo/WistiaCta';
import WebsiteImage from '../WebsiteImage/index';
import CallToAction from 'components/CallToAction';
import axios from 'axios';

const WistiaVideo = ({
  url,
  size,
  thumbnail,
  autoPlay,
  popoverCta,
  cta,
  ctaTitle,
  ctaBody,
  fullWidth,
  inline,
  onPlay,
  showThumbnailOnly,
  showPlayButtonOnly,
  label,
  labelProps,
  wrapperClassName,
  wrapperStyles,
  ...otherProps
}) => {
  const isClient = useIsClient();
  const wrapperRef = useRef();
  const videoRef = useRef();
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [ratio, setRatio] = useState();
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(!!autoPlay);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [videoReady, setVideoReady] = useState(!popoverCta || (isReady && popoverCta));
  const [showButton, setShowButton] = useState(showPlayButtonOnly || !inline || !isPlaying);

  const hasLabel = !!label;
  const useCallToAction = !!cta;
  const videoIsVisible = !hasLabel && !showPlayButtonOnly && !useCallToAction;
  const videoId = url?.substring(url.length - 10);
  const useImageSizing = inline ? false : !!thumbnail;
  const videoHeight =
    !useImageSizing && (size || fullWidth) && getVideoHeight(wrapperRef, ratio, size);
  const includesCta = popoverCta && ctaBody && ctaTitle;

  const handleOnPause = () => {
    setIsPlaying(false);

    const cta = document.querySelector(`#wistia-popover-cta-${videoId}`);

    if (cta) {
      cta.style.top = '-999px';
      cta.style.display = 'block !important';
    }
  };

  useEffect(() => {
    (async () => {
      if (thumbnail) {
        setVideoThumbnail(thumbnail?.image?.file?.url || thumbnail);
      } else if (videoIsVisible) {
        const formattedUrl = url?.trim();
        if (formattedUrl) {
          const data = (
            await axios.get(`https://fast.wistia.net/oembed?url=${formattedUrl}&embedType=async`)
          )?.data;
          if (data) {
            setVideoThumbnail(data.thumbnail_url);
            setRatio(data.width / data.height);
          }
        }
      }
    })();
  }, []);

  useEffect(() => setVideoReady(!popoverCta || (isReady && popoverCta)), [isReady]);

  useEffect(() => setShowButton(showPlayButtonOnly || !inline || !isPlaying), [isPlaying]);

  if (isClient) {
    return (
      <WistiaWrapper
        ref={wrapperRef}
        backgroundUrl={videoIsVisible && !useImageSizing && videoThumbnail}
        callToAction={useCallToAction}
        className={`wistia-video--wrapper ${wrapperClassName || ''}`}
        inline={inline}
        size={size}
        isPlaying={isPlaying}
        hasPlayed={hasPlayed}
        showPlayButtonOnly={showPlayButtonOnly}
        showThumbnailOnly={showThumbnailOnly}
        useImageSizing={useImageSizing}
        videoHeight={videoHeight}
        style={wrapperStyles}
        {...otherProps}
      >
        {videoReady &&
          (useCallToAction ? (
            <CallToAction
              {...cta}
              onClick={onPlay}
              wistiaVideo={null}
              setIsPlaying={setIsPlaying}
            />
          ) : hasLabel ? (
            <PlayLabel
              label={label}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              onClick={onPlay}
              {...labelProps}
            />
          ) : (
            !useCallToAction &&
            !showThumbnailOnly &&
            showButton && (
              <PlayButton
                isPlaying={isPlaying}
                onClick={onPlay}
                setHasPlayed={setHasPlayed}
                setIsPlaying={setIsPlaying}
              />
            )
          ))}
        {videoIsVisible && useImageSizing && <WebsiteImage src={videoThumbnail} />}
        <ReactPlayer
          ref={videoRef}
          controls={true}
          onPause={() => handleOnPause()}
          onReady={() => setIsReady(true)}
          playing={isPlaying}
          url={url}
          height={hasLabel || useCallToAction ? '0px' : '100%'}
          width={!hasLabel && !useCallToAction && '100%'}
          config={{
            wistia: {
              options: {
                popover: !inline,
                playButton: false,
                videoFoam: true,
                autoPlay: !!autoPlay,
                popoverPreventScroll: true,
                preload: 'none',
                popoverCaptionContainer: includesCta ? `wistia-popover-cta-${videoId}` : null,
              },
            },
          }}
        />
        {popoverCta && ctaBody && ctaTitle && (
          <WistiaCta
            videoHeight={videoHeight}
            isPlaying={isPlaying}
            videoId={videoId}
            ratio={ratio}
            videoRef={videoRef}
            ctaTitle={ctaTitle}
            ctaBody={ctaBody}
            callToAction={popoverCta}
          />
        )}
      </WistiaWrapper>
    );
  } else {
    return null;
  }
};

WistiaVideo.defaultProps = {
  inline: false,
};

export default WistiaVideo;
